import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import UserLayout from "../layout/UserLayout";
import API_BASE, { UPLOADS_BASE } from "../../api";

const Products = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [tea, setTea] = useState([]);
  const [coconut, setCoconut] = useState([]);
  const [spices, setSpices] = useState([]);
  const [rice, setRice] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolveData = (data) => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") {
      return (
        data.products ||
        data.data ||
        data.items ||
        data.list ||
        data.result ||
        data.rice ||
        data.spices ||
        data.tea ||
        data.coconut ||
        []
      );
    }
    return [];
  };

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/tea_manager/get_tea_products.php`).then((r) => r.json()).catch(() => []),
      fetch(`${API_BASE}/coconut_manager/get_coconut_products.php`).then((r) => r.json()).catch(() => []),
      fetch(`${API_BASE}/spices_manager/get_spices_products.php`).then((r) => r.json()).catch(() => []),
      fetch(`${API_BASE}/rice_manager/get_rice_products.php`).then((r) => r.json()).catch(() => []),
    ])
      .then(([teaData, coconutData, spicesData, riceData]) => {
        setTea(resolveData(teaData));
        setCoconut(resolveData(coconutData));
        setSpices(resolveData(spicesData));
        setRice(resolveData(riceData));
      })
      .catch((err) => console.error("Error loading products:", err))
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    { id: "all", label: "All Products" },
    { id: "tea", label: "Ceylon Tea", count: tea.length },
    { id: "coconut", label: "Coconut Essentials", count: coconut.length },
    { id: "spices", label: "Ceylon Spices", count: spices.length },
    { id: "rice", label: "Traditional Rice", count: rice.length },
  ];

  const sections = [
    {
      id: "tea",
      title: "Ceylon Tea Collection",
      subtitle: "Pure orthodox single-origin teas",
      data: tea,
      path: "tea",
      folder: "tea_products",
      exploreLink: "/tea"
    },
    {
      id: "coconut",
      title: "Ceylon Coconut Products",
      subtitle: "Virgin cold-pressed oils & coconut essentials",
      data: coconut,
      path: "coconut",
      folder: "coconut_products",
      exploreLink: "/coconut"
    },
    {
      id: "spices",
      title: "Ceylon Spices & True Cinnamon",
      subtitle: "Finest Alba & C5 cinnamon, black pepper, cloves",
      data: spices,
      path: "spices",
      folder: "spices_products",
      exploreLink: "/spices"
    },
    {
      id: "rice",
      title: "Traditional Sri Lankan Heirloom Rice",
      subtitle: "Nutrient-dense ancient organic grains",
      data: rice,
      path: "rice",
      folder: "rice_products",
      exploreLink: "/rice"
    },
  ];

  const filteredSections = activeTab === "all"
    ? sections
    : sections.filter((s) => s.id === activeTab);

  return (
    <UserLayout>
      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-emerald-950 via-teal-950 to-stone-950 text-white pt-32 pb-20 px-6 sm:px-12 text-center">
        <div className="max-w-4xl mx-auto">
          <span className="text-amber-400 font-semibold tracking-widest text-sm uppercase">
            Export Grade Ceylon Products
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold mt-3 mb-4 tracking-tight">
            Complete Product Portfolio
          </h1>
          <p className="text-lg text-emerald-100 max-w-2xl mx-auto leading-relaxed">
            Explore authentic Sri Lankan agro-exports — handcrafted, certified organic, and packaged for retail or bulk global distribution.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12">
        {/* Category Tabs */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-14">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeTab === cat.id
                  ? "bg-emerald-800 text-white shadow-md scale-105"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {cat.label}
              {cat.count !== undefined && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                  activeTab === cat.id ? "bg-emerald-700 text-white" : "bg-gray-200 text-gray-700"
                }`}>
                  {cat.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="py-24 text-center text-gray-500">
            <div className="inline-block w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p>Loading catalog items...</p>
          </div>
        ) : (
          filteredSections.map((section) => (
            <div key={section.id} className="mb-20">
              <div className="flex flex-col sm:flex-row items-baseline justify-between mb-8 pb-4 border-b border-gray-200">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                  <p className="text-sm text-gray-500 mt-1">{section.subtitle}</p>
                </div>
                <Link
                  to={section.exploreLink}
                  className="text-sm font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1.5 mt-2 sm:mt-0"
                >
                  <span>Explore {section.title.split(" ")[0]} Category</span>
                  <i className="fas fa-arrow-right text-xs"></i>
                </Link>
              </div>

              {section.data.length === 0 ? (
                <div className="py-12 text-center text-gray-400 bg-gray-50 rounded-2xl">
                  <p>No products currently available in this category.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                  {section.data.map((p) => {
                    const imgUrl = p.image
                      ? `${UPLOADS_BASE}/${section.folder}/${p.image}`
                      : null;
                    return (
                      <div
                        key={p.id}
                        className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group"
                      >
                        <div className="relative h-52 bg-gray-100 overflow-hidden">
                          {imgUrl ? (
                            <img
                              src={imgUrl}
                              alt={p.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <i className="fas fa-box-open text-3xl"></i>
                            </div>
                          )}
                          {p.net_weight && (
                            <span className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                              {p.net_weight}
                            </span>
                          )}
                        </div>

                        <div className="p-5 flex-1 flex flex-col justify-between">
                          <div>
                            {p.tea_grade && (
                              <span className="inline-block text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">
                                {p.tea_grade}
                              </span>
                            )}
                            <h3 className="text-base font-bold text-gray-900 group-hover:text-emerald-800 transition line-clamp-1">
                              {p.title}
                            </h3>
                            <p className="text-xs text-gray-600 mt-2 line-clamp-2 leading-relaxed">
                              {p.tasting_notes || p.description || "Authentic Ceylon harvest"}
                            </p>
                          </div>

                          <div className="mt-5 pt-3 border-t border-gray-100 flex items-center justify-between">
                            <Link
                              to={`/${section.path}/${p.id}`}
                              className="text-xs font-bold text-emerald-800 hover:text-emerald-950 flex items-center gap-1"
                            >
                              <span>Details</span>
                              <i className="fas fa-chevron-right text-[10px]"></i>
                            </Link>
                            <Link
                              to="/contact"
                              className="px-3 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition"
                            >
                              Inquire
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </UserLayout>
  );
};

export default Products;
